import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api';
import Breadcrumb from '../components/Breadcrumb';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import EditCustomerForm from '../components/EditCustomerForm';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentsIcon from '@mui/icons-material/Payments';
import { formatDate } from '../utils/dateUtils';
import { labelSx, metaTextSx, metaMonoSx, dateTextSx, pageContainerSx } from '../styles/tokens';
import PageLoader from '../components/ui/PageLoader';
import PageHeader from '../components/ui/PageHeader';

const statusPillSx = (status) => {
  const map = {
    ORDERED: { bg: 'rgba(245, 158, 11, 0.12)', color: '#b45309', border: '1px solid rgba(245, 158, 11, 0.3)' },
    DISPATCHED: { bg: 'rgba(59, 130, 246, 0.12)', color: '#1d4ed8', border: '1px solid rgba(59, 130, 246, 0.3)' },
    RECEIVED: { bg: 'rgba(16, 185, 129, 0.12)', color: '#047857', border: '1px solid rgba(16, 185, 129, 0.3)' },
    CANCELLED: { bg: 'rgba(239, 68, 68, 0.1)', color: '#b91c1c', border: '1px solid rgba(239, 68, 68, 0.2)' },
  };
  const s = map[status] || map.ORDERED;
  return {
    borderRadius: '9999px',
    bgcolor: s.bg,
    color: s.color,
    border: s.border,
    px: 1.25,
    py: 0.5,
    fontSize: '10px',
    fontWeight: 600,
    display: 'inline-block',
  };
};

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [custRes, bookingsRes] = await Promise.all([
        axios.get(`/api/customer/${id}`),
        axios.get(`/api/customer/${id}/bookings`),
      ]);
      setCustomer(custRes.data);
      setBookings(bookingsRes.data);
    } catch (e) {
      // simple error handling
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  if (loading) {
    return <PageLoader message="Loading customer details..." />;
  }

  if (!customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">Customer not found.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/customers')} sx={{ mt: 2, textTransform: 'none' }}>
          Back to Customers
        </Button>
      </Box>
    );
  }

  const phones = [customer.ph1, customer.ph2].filter(Boolean);
  const totalSpent = bookings.reduce((sum, b) => sum + (Number(b.final_price ?? b.total_amount) || 0), 0);
  const activeBookings = bookings.filter((b) => (b.status || '').toUpperCase() === 'ORDERED').length;
  const customerSince = '—'; // API does not return joined date

  const statCards = [
    { label: 'Total Bookings', value: bookings.length, icon: ShoppingCartIcon },
    { label: 'Active Bookings', value: activeBookings, icon: LocalShippingIcon },
    { label: 'Total Spent', value: `₹${totalSpent.toLocaleString('en-IN')}`, icon: PaymentsIcon },
    { label: 'Customer Since', value: customerSince, icon: CalendarTodayIcon },
  ];

  const profileFields = [
    { icon: BusinessIcon, label: 'Company', value: customer.company_name || '—' },
    { icon: EmailIcon, label: 'Email Address', value: customer.email || '—' },
    { icon: LocationOnIcon, label: 'Location', value: customer.place || customer.address || '—' },
  ];

  return (
    <Box sx={{ ...pageContainerSx, pb: 4, px: { xs: 1.5, sm: 2 } }}>
      <Breadcrumb items={[{ label: 'Customers', path: '/customers' }, { label: customer.name, path: null }]} />

      <PageHeader
        title={customer.company_name || customer.name}
        subtitle="Profile and booking history"
        actionLabel="Edit Customer"
        actionIcon={<EditIcon sx={{ fontSize: 16 }} />}
        onAction={() => setEditModalOpen(true)}
      />

      {/* Stats Row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {statCards.map((stat) => (
          <Paper
            key={stat.label}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: '0 1px 3px 0 rgba(0,0,0,0.08)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                {stat.label}
              </Typography>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.25,
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <stat.icon sx={{ fontSize: 14, color: 'text.secondary' }} />
              </Box>
            </Box>
            <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: 'text.primary', fontFamily: 'monospace', letterSpacing: 0 }}>
              {stat.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 3fr' },
          gap: 2,
          width: '100%',
          minWidth: 0,
        }}
      >
        {/* Profile Card - left (25% width on md+) */}
        <Box sx={{ minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: '0 1px 3px 0 rgba(0,0,0,0.08)',
              overflow: 'hidden',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                p: 2.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'text.primary',
                    color: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {(customer.name || ' ').charAt(0).toUpperCase()}
                </Box>
                <Box minWidth={0}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary', lineHeight: 1.45 }}>
                    {customer.name}
                  </Typography>
                  <Typography sx={{ ...metaTextSx, mt: 0.25 }}>
                    {customer.company_name || '—'}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setEditModalOpen(true)}
                sx={{
                  width: 32,
                  height: 32,
                  color: 'text.secondary',
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
                }}
                title="Edit customer"
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Box sx={{ p: 2.5, '& > * + *': { mt: 2 } }}>
              {profileFields.map((field) => (
                <Box key={field.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      flexShrink: 0,
                      borderRadius: 1.25,
                      bgcolor: 'action.hover',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: 0.25,
                    }}
                  >
                    <field.icon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </Box>
                  <Box>
                    <Typography sx={labelSx}>{field.label}</Typography>
                    <Typography sx={{ ...metaTextSx, mt: 0.25 }}>
                      {field.value}
                    </Typography>
                  </Box>
                </Box>
              ))}

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: 1.25,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 0.25,
                  }}
                >
                  <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                </Box>
                <Box>
                  <Typography sx={labelSx}>Phone Numbers</Typography>
                  <Box sx={{ mt: 0.25 }}>
                    {phones.length === 0 ? (
                      <Typography sx={{ ...metaTextSx }}>—</Typography>
                    ) : (
                      phones.map((phone) => (
                        <Typography key={phone} sx={{ ...metaMonoSx }}>
                          {phone}
                        </Typography>
                      ))
                    )}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    borderRadius: 1.25,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 0.25,
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                </Box>
                <Box>
                  <Typography sx={labelSx}>Address</Typography>
                  <Typography sx={{ ...metaTextSx, mt: 0.25 }}>
                    {customer.address || '—'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Booking History - right (75% width on md+) */}
        <Box sx={{ minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: '0 1px 3px 0 rgba(0,0,0,0.08)',
              overflow: 'hidden',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ShoppingCartIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', letterSpacing: '-0.01em' }}>
                    Booking History
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                    {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TrendingUpIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                  ₹{totalSpent.toLocaleString('en-IN')} total
                </Typography>
              </Box>
            </Box>

            <Box sx={{ p: 2.5 }}>
              {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {bookings.length === 0 ? (
                    <Typography align="center" sx={{ py: 4, color: 'text.secondary', fontSize: '0.9375rem' }}>
                      No bookings found
                    </Typography>
                  ) : (
                    bookings.map((b) => (
                      <Paper
                        key={b.id}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 1.25,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => navigate(`/bookings/${b.id}`)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                          <Typography sx={{ fontSize: '0.9375rem', color: 'text.primary', fontFamily: 'monospace' }}>
                            #{b.id}
                          </Typography>
                          <Box component="span" sx={statusPillSx(b.status || 'ORDERED')}>
                            {b.status || 'ORDERED'}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                          <Typography component="span" sx={dateTextSx}>Book: {formatDate(b.booking_date) || '—'}</Typography>
                          <Typography component="span" sx={dateTextSx}>Dispatch: {formatDate(b.dispatch_date) || '—'}</Typography>
                          <Typography component="span" sx={dateTextSx}>Return: {formatDate(b.return_date) || '—'}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mt: 0.75 }}>
                          ₹{((b.final_price ?? b.total_amount) != null ? Number(b.final_price ?? b.total_amount).toLocaleString('en-IN') : '—')}
                        </Typography>
                      </Paper>
                    ))
                  )}
                </Box>
              ) : (
                <TableContainer sx={{ borderRadius: 1.25, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 520 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell sx={{ py: 1.25, px: 2, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                          Order ID
                        </TableCell>
                        <TableCell sx={{ py: 1.25, px: 2, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                          Booking
                        </TableCell>
                        <TableCell sx={{ py: 1.25, px: 2, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                          Dispatch
                        </TableCell>
                        <TableCell sx={{ py: 1.25, px: 2, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                          Return
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.25, px: 2, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                          Amount
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1.25, px: 2, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bookings.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ py: 5, textAlign: 'center', fontSize: '0.9375rem', color: 'text.secondary' }}>
                            No bookings found
                          </TableCell>
                        </TableRow>
                      ) : (
                        bookings.map((b) => (
                          <TableRow
                            key={b.id}
                            sx={{
                              '&:last-child td': { borderBottom: 0 },
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <TableCell sx={{ py: 1.5, px: 2 }}>
                              <Typography component="span" sx={metaMonoSx}>#{b.id}</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, px: 2 }}>
                              <Typography component="span" sx={dateTextSx}>{formatDate(b.booking_date) || '—'}</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, px: 2 }}>
                              <Typography component="span" sx={dateTextSx}>{formatDate(b.dispatch_date) || '—'}</Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5, px: 2 }}>
                              <Typography component="span" sx={dateTextSx}>{formatDate(b.return_date) || '—'}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.5, px: 2, fontSize: '0.9375rem', color: 'text.primary', fontFamily: 'monospace' }}>
                              ₹{((b.final_price ?? b.total_amount) != null ? Number(b.final_price ?? b.total_amount).toLocaleString('en-IN') : '—')}
                            </TableCell>
                            <TableCell align="center" sx={{ py: 1.5, px: 2 }}>
                              <Box component="span" sx={statusPillSx(b.status || 'ORDERED')}>
                                {b.status || 'ORDERED'}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {editModalOpen && customer && (
        <EditCustomerForm
          customer={customer}
          onUpdate={async () => {
            const res = await axios.get(`/api/customer/${id}`);
            setCustomer(res.data);
          }}
          closeModal={() => setEditModalOpen(false)}
        />
      )}
    </Box>
  );
};

export default CustomerDetails;
